import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';
import { BackLink } from '@/components/ui/BackLink';
import { SectorToggle } from '@/components/ui/SectorSwitch';
import { useSectorInfo } from '@/lib/sector';
import { Logo } from '@/app/BuyerLayout';

/**
 * AppBar — the mobile and tablet top bar.
 *
 * Hidden at 1280+ where the desktop header takes over. Kept deliberately thin:
 * on a 393×852 screen every pixel above the fold is competing with the offer
 * ladder, which is the thing the buyer actually came for.
 *
 * ── WHY `back` RENDERS TWICE ────────────────────────────────────────────────
 *
 * This bar disappears at 1280px, and the desktop header that replaces it has no
 * back control — it is a site header, not a screen header, so it does not know
 * what "back" means. The result was that a buyer who went Account → Sell on
 * ChiCare on a laptop had no way back inside the page at all: the only exit was
 * the browser's own button, and on a screen reached by a tap on a link that is
 * not somewhere people look.
 *
 * So `back` also renders a slim in-page control that appears only at 1280+.
 * Same target, same handler, only one of them ever visible.
 *
 * ── AND WHY THE BRAND ROW CARRIES THE SHOP SWITCH ───────────────────────────
 *
 * It is the one row on a phone that already holds the wordmark and the cart, so
 * a third 44px control costs no vertical space at all — where the switch this
 * replaces was a full-width band of it, on every screen, for a control most
 * people touch once a week.
 *
 * Centred absolutely rather than with flex spacers, because the brand is about
 * a hundred pixels wide and the actions are forty-four: shared-out space would
 * put it visibly off-centre, and "in the middle" is the whole look.
 */

export interface AppBarProps {
  /** Brand lockup instead of a title. Home only. */
  brand?: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  back?: boolean;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export function AppBar({
  brand,
  title,
  subtitle,
  back,
  actions,
  className,
  children,
}: AppBarProps) {
  const navigate = useNavigate();

  return (
    <>
      {/* The desktop twin. Sits in the page flow rather than in a bar: at
          this width there is already a site header above it, and a second
          sticky strip would only eat the fold. */}
      {back ? (
        <div className="mx-auto hidden w-full max-w-[1200px] px-6 pt-5 xl:block">
          <BackLink />
        </div>
      ) : null}

      <header
        className={cn(
          'sticky top-0 z-20 border-b border-line-2 bg-white px-4 pb-3 pt-2 xl:hidden',
          className,
        )}
      >
        <div className="relative flex items-center gap-2.5">
          {back ? (
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="touch-target -ml-2 grid place-items-center rounded-[9px] text-muted hover:bg-paper"
            >
              <Icon name="back" size={18} strokeWidth={1.7} />
            </button>
          ) : null}

          {brand ? <BrandMark /> : (
            <div className="min-w-0 flex-1">
              {title ? <div className="truncate text-[13.5px] font-bold">{title}</div> : null}
              {subtitle ? <div className="truncate text-[11px] text-faint">{subtitle}</div> : null}
            </div>
          )}

          {brand ? <span className="flex-1" /> : null}

          {/* Dead centre of the bar, whatever is either side of it. */}
          {brand ? (
            <span className="pointer-events-none absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center">
              <span className="pointer-events-auto">
                <SectorToggle />
              </span>
            </span>
          ) : null}

          {actions}
        </div>

        {children}
      </header>
    </>
  );
}

/**
 * The wordmark, which says which shop this is.
 *
 * "ChiCare Vet" on the veterinary side, "ChiCare" on the pharmacy side — the
 * same suffix the desktop header has always carried, and which the phone bar
 * was missing entirely. That gap did not matter while a switch below it spelled
 * out both shops in words; it matters a great deal now that the switch is an
 * icon, because this is what a buyer reads instead.
 */
function BrandMark() {
  const info = useSectorInfo();

  return (
    <span className="flex items-center gap-1.5 font-display text-[17px] font-extrabold tracking-[-0.02em] text-blue">
      <span className="grid h-[22px] w-[22px] place-items-center rounded-[7px] bg-blue">
        <Logo className="h-[13px] w-[13px] fill-white" />
      </span>
      ChiCare
      {info.wordmark ? (
        <span
          data-testid="appbar-wordmark"
          className="font-mono text-[10px] font-bold uppercase tracking-[0.14em]"
        >
          {info.wordmark}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A 44px icon control in the app bar — a link when it goes somewhere.
 *
 * `to` renders an anchor rather than a button, which is what these almost
 * always are. Several of them shipped as bare `<button>`s with no handler at
 * all, so Cart in the top bar looked tappable and did nothing on every screen
 * that had one.
 *
 * `dot` is a real unread marker, not decoration. Pass it only from something
 * that knows: a permanent red dot on an empty cart is a lie the buyer notices
 * on their first tap.
 */
export function AppBarButton({
  icon,
  label,
  dot = false,
  to,
  onClick,
}: {
  icon: IconName;
  label: string;
  dot?: boolean;
  to?: string;
  onClick?: () => void;
}) {
  const className =
    'relative grid h-11 w-11 shrink-0 place-items-center rounded-[9px] text-muted no-underline hover:bg-paper hover:text-ink';

  const content = (
    <>
      <Icon name={icon} size={18} strokeWidth={1.7} />
      {dot ? (
        <span className="absolute right-[5px] top-[5px] h-[7px] w-[7px] rounded-full border-[1.5px] border-white bg-danger" />
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link to={to} aria-label={label} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-label={label} className={className}>
      {content}
    </button>
  );
}
